import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import QRCode from 'qrcode.react';

import Toast from '../../../components/Toast/Toast';
import { logging } from '../../../actions/log';
import Dialog from '../../../components/Dialog/Dialog';
import styles from './EduDetailPay.module.less';
import {
  getEducationDetail,
  postEducationPay,
  getEducationPayResult,
  setEducationPay,
  setEducationPayResult,
} from '../../../actions/education';
import { getQueryString, logNative } from '../../../utils/utils';

const selectPayCreate = createSelector(
  state => state.education,
  education => education.payCreate,
);

const selectPayResult = createSelector(
  state => state.education,
  education => education.payResult,
);

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

let EduGetPayIndex = 1;
let EduTimeIndex = 0;
let EduDateKey = +new Date();

export default ({ handleClose = () => {}, detailsData }) => {
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  const payCreate = useSelector(selectPayCreate);
  const payResult = useSelector(selectPayResult);

  const courseId = getQueryString('courseId');

  useEffect(() => {
    // 直接下单, 获取支付信息
    getPayInfo();

    dispatch(
      logging({
        category: 'I',
        action: 'Edu_PayBox',
        label: '',
        param2: {
          source: 'Edu_Detail',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          courseid: detailsData.data.courseId,
          discountPrice: detailsData.data.discountPrice,
          price: detailsData.data.price,
        },
      }),
    );
  }, [dispatch, detailsData]);

  // 课程下单，获取下单后的支付信息
  const getPayInfo = useCallback(() => {
    dispatch(
      postEducationPay({
        appType: '11',
        goodsType: 'course',
        sku: detailsData.data.courseId,
        amount: detailsData.data.discountPrice,
        name: detailsData.data.name,
      }),
    );
  }, [detailsData]);

  // 下单后, 每隔2秒获取一次支付是否成功
  useEffect(() => {
    if (
      payCreate &&
      payCreate.data &&
      payCreate.status === 0 &&
      payCreate.data.payUrl &&
      payCreate.data.queryUrl
    ) {
      dispatch(
        getEducationPayResult({
          queryUrl: payCreate.data.queryUrl,
          courseId: detailsData.data.courseId,
          index: ++EduGetPayIndex,
        }),
      );
    }
  }, [dispatch, payCreate]);

  useEffect(() => {
    if (
      payCreate &&
      payCreate.data &&
      payCreate.status === 0 &&
      payCreate.data.payUrl &&
      payCreate.data.queryUrl &&
      payResult &&
      detailsData.data.courseId === payCreate.sku &&
      detailsData.data.courseId === payResult.courseId
    ) {
      clearTimeout(EduTimeIndex);
      // 每隔2秒获取一次是否支付成功，成功则刷新页面
      if (payResult.status === -2) {
        EduTimeIndex = setTimeout(() => {
          dispatch(
            getEducationPayResult({
              queryUrl: payCreate.data.queryUrl,
              courseId: detailsData.data.courseId,
              index: ++EduGetPayIndex,
            }),
          );
        }, 2000);
      } else if (payResult.status === 0) {
        Toast.success({ content: '购买成功！' });
        logNative(
          `视频购买，支付成功 ${{
            courseid: detailsData.data.courseId,
            discountPrice: detailsData.data.discountPrice,
            price: detailsData.data.price,
            status: payResult.status,
          }}`,
          {},
          true,
        );
        dispatch(
          logging({
            category: 'R',
            action: 'Edu_Pay_Result',
            label: '',
            param2: {
              source: 'Edu_Detail',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              courseid: detailsData.data.courseId,
              discountPrice: detailsData.data.discountPrice,
              price: detailsData.data.price,
              status: payResult.status,
            },
          }),
        );
        if (EduDateKey !== detailsData.time) {
          // 获取视频详情页面数据
          dispatch(getEducationDetail({ courseId, time: EduDateKey }));
          dispatch(setEducationPay({ data: null }));
          dispatch(setEducationPayResult({ data: null }));
          handleClose('success');
        }
      } else if (payResult.status === -1) {
        Toast.success({ content: '购买失败！' });
        dispatch(
          logging({
            category: 'R',
            action: 'Edu_Pay_Result',
            label: '',
            param2: {
              source: 'Edu_Detail',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              courseid: detailsData.data.courseId,
              discountPrice: detailsData.data.discountPrice,
              price: detailsData.data.price,
              status: payResult.status,
            },
          }),
        );
      }
    }
  }, [dispatch, detailsData, payCreate, payResult]);

  const handleUpdate = useCallback(() => {
    getPayInfo();
  }, []);

  const handlePayClose = useCallback(() => {
    dispatch(
      logging({
        category: 'C',
        action: 'Edu_Pay_Close',
        label: '',
        param2: {
          source: 'Edu_Detail',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          courseid: detailsData.data.courseId,
          discountPrice: detailsData.data.discountPrice,
          price: detailsData.data.price,
        },
      }),
    );
    dispatch(setEducationPay({ data: null }));
    dispatch(setEducationPayResult({ data: null }));
    handleClose();
  }, []);

  if (detailsData && detailsData.data && payCreate && detailsData.data.courseId === payCreate.sku) {
    return (
      <Dialog
        title={`支付订单：${detailsData.data.name}`}
        handleClose={handlePayClose}
        maskStyle={styles.mask}
        containerStyle={styles.container}
      >
        <div className={styles.pay}>
          <div className={styles.pay_info}>
            <div className={styles.pay_info_title}>支付金额：</div>
            {detailsData.data.discountPrice > 0 && detailsData.data.price && (
              <>
                <div className={styles.pay_info_mark}>￥</div>
                <div className={styles.pay_info_price}>
                  {(detailsData.data.discountPrice / 100).toFixed(2)}
                </div>
                <div className={styles.pay_info_old_price}>
                  {(detailsData.data.price / 100).toFixed(2)}
                </div>
              </>
            )}
            {detailsData.data.discountPrice === 0 && detailsData.data.price && (
              <>
                <div className={styles.pay_info_mark}>￥</div>
                <div className={styles.pay_info_price}>
                  {(detailsData.data.price / 100).toFixed(2)}
                </div>
              </>
            )}
          </div>
          <div className={styles.pay_QRcode}>
            {payCreate && payCreate.data && payCreate.data.payUrl && payCreate.status === 0 ? (
              <QRCode className="qrcode_image" size={120} level="Q" value={payCreate.data.payUrl} />
            ) : (
              <div className={styles.pay_QRcode_err} onClick={handleUpdate}>
                二维码获取失败，<span>刷新</span>
              </div>
            )}
          </div>
          <div className={styles.pay_desc}>请使用微信/支付宝扫码，轻松支付</div>
        </div>
      </Dialog>
    );
  } else {
    return '';
  }
};
